import React from 'react';
import DateParser from 'utils/date/DateParser';
import DateFormatter from 'utils/date/DateFormatter';
import NavigationUtil from 'utils/NavigationUtil';
import { isLimitedAnnualBadge, isRepeatableBadge, isRetiredBadge } from '../../../utils/BadgeUtils';
import { BADGE_ASSOC_TYPE_KEYS, BADGE_CATEGORY_KEYS } from '../../../constants/BadgesConstants';
import { fetchRepeatableBadgeDetails } from '../../../actions/badgesActions';
import { Spinner } from 'ui-connect';
import Translate from 'components/Translate';

class BadgeEarnedMessage extends React.Component {
    constructor() {
        super();

        this.state = {
            open: false,
            earnedDetails: null
        };
    }

    render() {
        const { badge, isConnection } = this.props;

        if (badge.displayName === null) {
            if (!isConnection) {
                return (
                    <div className="badge-earned-msg">
                        <Translate ns="common" content="badge_not_earned_status" />
                    </div>
                );
            }
            return null;
        }

        if (isRepeatableBadge(badge) && badge.badgeEarnedNumber > 1) {
            return this.renderRepeatableMessage();
        } else {
            return <div className="badge-earned-msg">{this.renderSingularMessage()}</div>;
        }
    }

    renderSingularMessage() {
        const { badge } = this.props;
        const date = DateFormatter.formatLongMonthDayYear(DateParser.parseISOUTC(badge.badgeEarnedDate)); // format: December 6, 2017
        let badgeAssocTypeKey = this.getBadgeAssocTypeKey(badge.badgeAssocTypeId);
        const badgeCategoryKey = this.getBadgeCategoryKey(badge);

        if (
            badgeAssocTypeKey === BADGE_ASSOC_TYPE_KEYS.NO_LINK ||
            !badge.badgeAssocDataId ||
            badgeAssocTypeKey === BADGE_ASSOC_TYPE_KEYS.BADGE_CHALLENGE
        ) {
            return this.renderEarnedString(date);
        }

        let name, url;
        switch (badgeAssocTypeKey) {
            case BADGE_ASSOC_TYPE_KEYS.ACTIVITY:
                name = badge.badgeAssocDataName || <Translate ns="activity-api" content="untitled" />;
                url = `/activity/${badge.badgeAssocDataId}`;
                break;
            case BADGE_ASSOC_TYPE_KEYS.GROUP_CHALLENGE:
                name = badge.badgeAssocDataName;
                url = `/challenge/${badge.badgeAssocDataId}`;
                break;
            case BADGE_ASSOC_TYPE_KEYS.ADHOC_CHALLENGE:
                name = badge.badgeAssocDataName;
                url = `/adhoc-challenge/${badge.badgeAssocDataId}`;
                break;
            case BADGE_ASSOC_TYPE_KEYS.VIVOFITJR_CHALLENGE:
                name = badge.badgeAssocDataName;
                url = `/vivofitjr-challenge/${badge.badgeAssocDataId}`;
                break;
            case BADGE_ASSOC_TYPE_KEYS.VIVOFITJR_TEAM_CHALLENGE:
                name = badge.badgeAssocDataName;
                url = `/vivofitjr-teamchallenge/${badge.badgeAssocDataId}`;
                break;
            case BADGE_ASSOC_TYPE_KEYS.DAY:
                name = DateFormatter.formatLongMonthDayYear(DateParser.parseISO(badge.badgeAssocDataId)); // format: December 6, 2017
                url = this.getDateRedirectUrl(badgeCategoryKey, badge.badgeAssocDataId);
                break;
            default:
                break;
        }
        if (name && url) {
            let earnedActivity = this.getAnchorForTranslation(name, url);
            return this.renderEarnedString(date, earnedActivity);
        }
        if (badgeAssocTypeKey === BADGE_ASSOC_TYPE_KEYS.DAY && name) {
            return this.renderEarnedString(date);
        }

        return null;
    }

    renderEarnedString(earned, earnedActivity) {
        const { isConnection, badge } = this.props;
        const connectionName = badge.fullName || badge.displayName;

        return isConnection ? (
            earnedActivity ? (
                <Translate
                    ns="common"
                    content="badge_earned_connection_name"
                    params={{ 0: earned, 1: earnedActivity, 2: connectionName }}
                />
            ) : (
                <Translate
                    ns="common"
                    content="badge_earned_connection_date"
                    params={{ 0: earned, 1: connectionName }}
                />
            )
        ) : earnedActivity ? (
            <Translate ns="common" content="badge_earned_name" params={{ 0: earned, 1: earnedActivity }} />
        ) : (
            <Translate ns="common" content="badge_earned_date" params={{ 0: earned }} />
        );
    }

    getAnchorForTranslation(name, url) {
        return (
            <a href={NavigationUtil.url(url)} onClick={(e) => NavigationUtil.anchorTagHandler(e)}>
                {name}
            </a>
        );
    }

    getBadgeAssocTypeKey(badgeAssocTypeId) {
        const { badgesAttributes } = this.props;

        return badgesAttributes.badgeAssocTypes.find((assoc) => assoc.badgeAssocTypeId === badgeAssocTypeId)
            .badgeAssocTypeKey;
    }

    renderRepeatableMessage() {
        const { badge, isConnection } = this.props;

        return (
            <div className="badge-earned-msg">
                {isConnection ? (
                    <Translate
                        ns="common"
                        content="badge_earned_connection_amount"
                        params={{ 0: badge.badgeEarnedNumber, 1: badge.fullName || badge.displayName }}
                    />
                ) : (
                    <Translate ns="common" content="badge_earned_amount" params={{ 0: badge.badgeEarnedNumber }} />
                )}
                &nbsp;
                {this.renderEarnedLimit()}
                <br />
                <a onClick={(e) => this.setState({ open: !this.state.open })}>
                    {this.state.open ? (
                        <span id="hide-link">
                            <Translate ns="training_plans" content="training_plans_hide_details" />
                        </span>
                    ) : (
                        <span id="view-link">
                            <Translate ns="workout" content="view_details" />
                        </span>
                    )}
                </a>
                {this.state.open ? (
                    <ul className="badge-earned-msg-list collapse in">{this.renderRepeatableMessageDetails()}</ul>
                ) : null}
            </div>
        );
    }

    renderEarnedLimit = () => {
        const { badge } = this.props;
        const isRetired = isRetiredBadge(badge);
        const isRepeatable = isRepeatableBadge(badge);
        const isLimitedAnnual = isLimitedAnnualBadge(badge);
        if (isRetired || (isRepeatable && isLimitedAnnual) || !badge.badgeLimitCount || badge.badgeLimitCount <= 1) {
            return null;
        }
        return <Translate ns="common" content="badge_earned_limit" params={{ 0: badge.badgeLimitCount }} />;
    };

    renderRepeatableMessageDetails() {
        const { displayName, badge } = this.props;

        if (!this.state.earnedDetails) {
            fetchRepeatableBadgeDetails(displayName, badge.badgeId, 1, 1000).then((response) => {
                this.setState({
                    earnedDetails: response
                });
            });

            return (
                <div className="textCenter">
                    <Spinner />
                </div>
            );
        }

        const badgeCategoryKey = this.getBadgeCategoryKey(badge);

        return this.state.earnedDetails.map((item, index) => {
            const date = DateFormatter.formatMonthDayYear(DateParser.parseISOUTC(item.badgeEarnedDate));
            let url,
                name,
                dateLink = false;

            switch (this.getBadgeAssocTypeKey(item.badgeAssocTypeId)) {
                case BADGE_ASSOC_TYPE_KEYS.ACTIVITY:
                    if (item.badgeAssocDataId) {
                        url = `/activity/${item.badgeAssocDataId}`;
                        name = item.badgeAssocDataName || <Translate ns="activity-api" content="untitled" />;
                    }
                    break;
                case BADGE_ASSOC_TYPE_KEYS.GROUP_CHALLENGE:
                    if (item.badgeAssocDataId) {
                        url = `/challenge/${item.badgeAssocDataId}`;
                        name = item.badgeAssocDataName;
                    }
                    break;
                case BADGE_ASSOC_TYPE_KEYS.ADHOC_CHALLENGE:
                    if (item.badgeAssocDataId) {
                        url = `/adhoc-challenge/${item.badgeAssocDataId}`;
                        name = item.badgeAssocDataName;
                    }
                    break;
                case BADGE_ASSOC_TYPE_KEYS.VIVOFITJR_CHALLENGE:
                    if (item.badgeAssocDataId) {
                        url = `/vivofitjr-challenge/${item.badgeAssocDataId}`;
                        name = item.badgeAssocDataName;
                    }
                    break;
                case BADGE_ASSOC_TYPE_KEYS.VIVOFITJR_TEAM_CHALLENGE:
                    if (item.badgeAssocDataId) {
                        url = `/vivofitjr-teamchallenge/${item.badgeAssocDataId}`;
                        name = item.badgeAssocDataName || <Translate ns="activity-api" content="untitled" />;
                    }
                    break;
                case BADGE_ASSOC_TYPE_KEYS.DAY:
                    if (item.badgeAssocDataId) {
                        const urlDate = DateFormatter.formatISODate(DateParser.parseISOUTC(item.badgeEarnedDate)); // format: 2017-12-06
                        url = this.getDateRedirectUrl(badgeCategoryKey, urlDate);
                        dateLink = !!url;
                    }
                    break;
                default:
                    break;
            }

            return (
                <li key={index}>
                    <div className="badge-date">
                        {dateLink ? (
                            <a href={NavigationUtil.url(url)} onClick={(e) => NavigationUtil.anchorTagHandler(e)}>
                                {date}
                            </a>
                        ) : (
                            date
                        )}
                    </div>
                    <div className="badge-event truncate">
                        {name ? (
                            <a href={NavigationUtil.url(url)} onClick={(e) => NavigationUtil.anchorTagHandler(e)}>
                                {name}
                            </a>
                        ) : null}
                    </div>
                </li>
            );
        });
    }

    getBadgeCategoryKey = (badge) => {
        const { badgesAttributes } = this.props;

        if (!badge || !badgesAttributes?.badgeCategories) {
            return null;
        }

        const badgeCategory = badgesAttributes.badgeCategories.find(
            (item) => item.badgeCategoryId === badge.badgeCategoryId
        );
        return badgeCategory?.badgeCategoryKey || null;
    };

    getDateRedirectUrl = (badgeCategoryKey, urlDate) => {
        let url = '';

        switch (badgeCategoryKey) {
            case BADGE_CATEGORY_KEYS.STEPS:
                url = `/steps/${urlDate}`;
                break;
            default:
                break;
        }

        return url;
    };
}

export default BadgeEarnedMessage;
